/**
 * The three dedicated sections: water, air, noise.
 *
 * These sit outside the two columns because each is a single question that
 * deserves its own full-width treatment, and because two of them do not
 * actually differ by scenario in the way the money does. Water is a permitted
 * ceiling at a site; noise is a margin against a standard. Squeezing them into
 * a comparison cell would have made them look like money.
 */

import { evaluateMetric } from '../engine/evaluate.js';
import type { Metric, Pack, Range } from '../engine/schema.js';
import { buildScope, comparatorDivisor } from '../engine/scope.js';
import { formatValue, roundToSigFigs } from '../engine/units.js';
import type { Footnotes } from './footnotes.js';
import { WATER_ICONS, iconFacility } from './icons.js';
import { esc, num } from './num.js';
import { categoryTag } from './outcomes.js';
import type { AppState } from './store.js';

// ---------------------------------------------------------------- water

export function waterSection(pack: Pack, state: AppState, fn: Footnotes): string {
  const metric = pack.metrics.find((m) => m.id === 'water_use');
  if (!metric) return '';

  const scopeState = { scenarioId: 'built', params: {}, overrides: state.overrides };
  const result = evaluateMetric(pack, metric, scopeState);
  if (result.blocked) return '';
  const subject = (result.value as Range).mid;
  const scope = buildScope(pack, scopeState);

  const cards = (metric.comparatorIds ?? [])
    .map((id) => {
      const c = pack.comparators.find((x) => x.id === id);
      if (!c) return '';
      const divisor = comparatorDivisor(c, scope);
      if (divisor === undefined) return '';
      const multiple = subject / divisor;
      const shown = formatValue(roundToSigFigs(multiple, 2), 'count');
      const measured = c.basis === 'measured-actual';

      return `<figure class="wcard ${measured ? 'measured' : ''}">
        <div class="wcard-icon">${WATER_ICONS[c.id] ?? ''}</div>
        <figcaption>
          <span class="wcard-n mono">${esc(shown)}${c.verb === 'times' ? '×' : ''}</span>
          <span class="wcard-t">${esc(c.plainLabel)}</span>
          <span class="wcard-basis">${esc(
            measured ? 'measured actual' : 'permitted maximum',
          )}</span>
          ${c.note ? `<span class="wcard-note">${esc(c.note)}</span>` : ''}
        </figcaption>
      </figure>`;
    })
    .join('');

  return `<section class="feature water" id="water"><div class="container">
    ${categoryTag(metric.category)}
    <h2 class="section-title">${esc(metric.question)}${fn.ref(metric.id)}</h2>

    <div class="water-head">
      <div class="water-subject">
        <div class="wcard-icon big">${iconFacility}</div>
        ${num(result, { drawerId: metric.id, compact: true })}
        <p class="water-subject-label">${esc(metric.label)}</p>
      </div>
      <p class="prose water-lead">The facility figure is a <strong>permitted ceiling at a site that has withdrawn nothing yet</strong>. Three of the five comparisons below are measured actuals, and setting a ceiling against an actual overstates the gap.</p>
    </div>

    <div class="wgrid">${cards}</div>

    <ul class="caveat-list">
      ${metric.caveats.map((c) => `<li>${esc(c)}</li>`).join('')}
    </ul>
  </div></section>`;
}

// ---------------------------------------------------------------- air

export function airSection(pack: Pack, state: AppState, fn: Footnotes): string {
  const ids = ['so2_avoided', 'co2e_avoided'];
  const metrics = ids
    .map((id) => pack.metrics.find((m) => m.id === id))
    .filter((m): m is Metric => Boolean(m));
  if (!metrics.length) return '';

  return `<section class="feature air" id="air"><div class="container">
    ${categoryTag(metrics[0]!.category)}
    <h2 class="section-title">Does it matter, for the air, where this gets built?</h2>
    <p class="lede">Not built here does not mean not built. The comparison is Minnesota's grid against a coal-heavier one, because that is the choice actually on the table.</p>

    <div class="air-grid">
      ${metrics
        .map((m) => {
          const r = evaluateMetric(pack, m, {
            scenarioId: 'built',
            params: {},
            overrides: state.overrides,
          });
          return `<article class="air-card">
            <h3>${esc(m.question)}${fn.ref(m.id)}</h3>
            ${num(r, { drawerId: m.id, compact: true })}
            <p class="air-label">${esc(m.label)}</p>
            <ul class="caveat-list small">
              ${m.caveats.slice(0, 2).map((c) => `<li>${esc(c)}</li>`).join('')}
            </ul>
          </article>`;
        })
        .join('')}
    </div>

    <p class="prose air-note">Of the two, the sulphur figure is the more robust. On eGRID's non-baseload rates, the intended proxy for new incremental load, the carbon gap between the two grids very nearly closes, while the sulphur gap survives on either basis.</p>
  </div></section>`;
}

// ---------------------------------------------------------------- noise

export function noiseSection(pack: Pack, state: AppState, fn: Footnotes): string {
  const metric = pack.metrics.find((m) => m.id === 'noise_margin');
  if (!metric) return '';

  const r = evaluateMetric(pack, metric, {
    scenarioId: 'built',
    params: {},
    overrides: state.overrides,
  });

  return `<section class="feature noise" id="noise"><div class="container">
    ${categoryTag(metric.category)}
    <h2 class="section-title">${esc(metric.question)}${fn.ref(metric.id)}</h2>

    <div class="noise-body">
      <div class="noise-figure">
        ${num(r, { drawerId: metric.id, compact: true })}
        <p class="noise-label">${esc(metric.label)}</p>
      </div>
      <div>
        <p class="prose">The project's own materials compare a 49 dBA night-time estimate against a limit of 55. That is the L10 standard, which permits a level exceeded no more than 10% of the hour. A facility that runs continuously is an L50 source, and the L50 night-time standard is 50. The honest margin is one decibel, not six.</p>
      </div>
    </div>

    <ul class="caveat-list">
      ${metric.caveats.map((c) => `<li>${esc(c)}</li>`).join('')}
    </ul>
  </div></section>`;
}
